
import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Client } from '@/types/client';
import { Pencil, Search, Loader2 } from 'lucide-react';
import { apiService } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

interface SystemSettingsSectionProps {
  form: UseFormReturn<Client>;
  onRequestEditAsaasId?: () => void;
  isEditing?: boolean;
  clientId?: number;
  companyTaxId?: string;
  onAtomosChatAccountIdFetched?: (id: string) => void;
}

function maskAsaasId(value: string | null | undefined): string {
  const s = String(value ?? '').trim();
  if (s.length <= 3) return s ? s + '****' : '–';
  return s.slice(0, 3) + '****';
}

export const SystemSettingsSection: React.FC<SystemSettingsSectionProps> = ({
  form,
  onRequestEditAsaasId,
  isEditing = false,
  clientId,
  companyTaxId = '',
  onAtomosChatAccountIdFetched,
}) => {
  const [loadingAtomosChat, setLoadingAtomosChat] = useState(false);
  const canFetchAtomosChat = Boolean(clientId) && Boolean((companyTaxId ?? '').trim());

  const handleFetchAtomosChatAccountId = async () => {
    if (!clientId || !canFetchAtomosChat) return;
    setLoadingAtomosChat(true);
    try {
      const res = await apiService.fetchAtomosChatAccountId(clientId);
      const id = res?.atomos_chat_account_id ?? '';
      if (id) {
        form.setValue('atomos_chat_account_id', id);
        onAtomosChatAccountIdFetched?.(id);
        toast({ title: 'Sucesso', description: 'ID do ÁtomosChat encontrado e salvo.' });
      }
    } catch (e: any) {
      const msg = e?.message || e?.response?.data?.error || 'Erro ao buscar ID do ÁtomosChat';
      toast({ title: 'Erro', description: msg, variant: 'destructive' });
    } finally {
      setLoadingAtomosChat(false);
    }
  };

  return (
    <div className="bg-white p-6 border border-gray-200 rounded-lg">
      <h3 className="text-lg font-montserrat font-semibold text-primary-700 mb-4 flex items-center">
        <div className="w-2 h-2 bg-primary-500 rounded-full mr-2" />
        Configurações do Sistema
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={form.control}
          name="system_management"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Sistema de Gestão</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900">
                    <SelectValue placeholder="Selecione o sistema" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="hinova">Hinova</SelectItem>
                  <SelectItem value="south">South</SelectItem>
                  <SelectItem value="south-v0">South V0</SelectItem>
                  <SelectItem value="siprove">Siprove</SelectItem>
                  <SelectItem value="epta">Epta</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="keyword"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Palavra-chave</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="keyword"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="schedule_start_time"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Horário de Início</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900">
                    <SelectValue placeholder="Selecione o horário" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="bg-white border-gray-200">
                  {Array.from({ length: 24 }, (_, i) => i + 1).map((hour) => (
                    <SelectItem key={hour} value={hour.toString()}>
                      {hour.toString().padStart(2, '0')}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="system_user"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Usuário do Sistema</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="usuario"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="system_password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Senha do Sistema</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="password"
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="••••••••"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="api_base_url"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">URL Base da API</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="https://api.exemplo.com"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="asaas_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">ID Asaas (Customer ID)</FormLabel>
              {isEditing && onRequestEditAsaasId ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-9 w-full max-w-[280px] items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-700 font-mono">
                      {maskAsaasId(field.value)}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onRequestEditAsaasId}
                      className="text-gray-700 border-gray-200 hover:bg-gray-50 shrink-0"
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  </div>
                  <FormControl>
                    <input type="hidden" {...field} value={field.value ?? ''} />
                  </FormControl>
                </>
              ) : (
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900 font-mono"
                    placeholder="cus_000000000000"
                  />
                </FormControl>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="trello_card"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Card do Trello</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="trello_123456"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="account_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">Account ID ConnectChat</FormLabel>
              <FormControl>
                <Input 
                  {...field} 
                  type="number"
                  className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900"
                  placeholder="20"
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="atomos_chat_account_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-gray-700 font-medium">ID do ÁtomosChat (v2)</FormLabel>
              <div className="flex items-center gap-2">
                <FormControl>
                  <Input
                    {...field}
                    value={field.value ?? ''}
                    className="bg-white border-gray-200 focus:border-primary-400 focus:ring-primary-400/20 text-gray-900 font-mono"
                    placeholder="80f34938-90a0-48b2-9a8b-3c7329e9cb21"
                  />
                </FormControl>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFetchAtomosChatAccountId}
                  disabled={!canFetchAtomosChat || loadingAtomosChat}
                  className="text-gray-700 border-gray-200 hover:bg-gray-50 shrink-0"
                >
                  {loadingAtomosChat ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-1" />
                  )}
                  Buscar ID do cliente ÁtomosChat
                </Button>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};
